import { PDFDocument } from 'pdf-lib';
import axios from 'axios';

export const euroCountryCodes = {
  de: 'Germany',
  at: 'Austria',
  be: 'Belgium',
  bg: 'Bulgaria',
  hr: 'Croatia',
  cy: 'Cyprus',
  cz: 'Czech Republic',
  dk: 'Denmark',
  ee: 'Estonia',
  fi: 'Finland',
  fr: 'France',
  gr: 'Greece',
  hu: 'Hungary',
  ie: 'Ireland',
  it: 'Italy',
  lv: 'Latvia',
  lt: 'Lithuania',
  lu: 'Luxemborg',
  mt: 'Malta',
  nl: 'Netherlands',
  pl: 'Poland',
  pt: 'Portugal',
  ro: 'Romania',
  sk: 'Slovakia',
  si: 'Slovenia',
  es: 'Spain',
  se: 'Sweden',
  world: 'World',
};

export async function mergePDF(URLs: string[]): Promise<Blob> {
  // Fetch the pdf documents
  const fetchDocuments = URLs.map((url) =>
    axios
      .get(url, {
        responseType: 'arraybuffer',
      })
      .then((res) => PDFDocument.load(res.data))
  );
  const documents = await Promise.all(fetchDocuments);

  // Create new pdf doc
  const mergedPDF = await PDFDocument.create();

  // Copy the pages of each document
  const pagesToCopy = documents.map((document) => {
    const pages = document.getPageIndices();
    return mergedPDF.copyPages(document, pages);
  });
  const copiedPages = await Promise.all(pagesToCopy);

  // Add the pages to the new doc
  copiedPages.forEach((item) => {
    mergedPDF.addPage(item[0]);
  });

  // Save the document
  return new Blob([await mergedPDF.save()], { type: 'application/pdf' });
}

export function parseBytes(data: string, contentType: string): string {
  const sliceSize = 1024;
  const byteCharacters = atob(data);
  const bytesLength = byteCharacters.length;
  const slicesCount = Math.ceil(bytesLength / sliceSize);
  const byteArrays = new Array(slicesCount);

  for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
    const begin = sliceIndex * sliceSize;
    const end = Math.min(begin + sliceSize, bytesLength);

    const bytes = new Array(end - begin);
    for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
      bytes[i] = byteCharacters[offset].charCodeAt(0);
    }
    byteArrays[sliceIndex] = new Uint8Array(bytes);
  }
  const blob = new Blob(byteArrays, { type: contentType });
  return URL.createObjectURL(blob);
}

export async function readText(file: File): Promise<string> {
  return new Promise<string>((res, rej) => {
    const reader = new FileReader();
    reader.onerror = () => rej(reader.error);
    reader.onload = () => res(reader.result as string);
    reader.readAsText(file);
  });
}

export async function readBytes(file: File): Promise<string> {
  return new Promise<string>((res, rej) => {
    const reader = new FileReader();
    reader.onerror = () => rej(reader.error);
    reader.onload = () => res(btoa(reader.result as string));
    reader.readAsBinaryString(file);
  });
}

export async function readBytesUrl(file: File, appendFileType?: boolean): Promise<string> {
  return new Promise<string>((res, rej) => {
    const reader = new FileReader();
    reader.onerror = () => rej(reader.error);
    reader.onload = () => {
      let result = reader.result as string;
      if (!appendFileType) {
        result = result.substring(result.indexOf(',') + 1);
      }
      res(result);
    };
    reader.readAsDataURL(file);
  });
}

export function parseJsonString(jsonString?: string | null): Record<string, JSONValue> {
  if (jsonString && jsonString.trim().length > 0) {
    return JSON.parse(jsonString);
  }
  return {};
}
